import { useEffect, useState, type FormEvent } from "react";
import { Link } from "react-router-dom";
import { PageHeader, Panel } from "../components/Surface";
import { Button } from "../components/ui/Button";
import { inputClass } from "../components/FormKit";
import { labelize } from "../lib/format";
import {
  listListings, createListing, priceUsd, LISTING_CATEGORIES, LISTING_CONDITIONS, type Listing,
} from "../lib/marketplace";

export function Marketplace() {
  const [listings, setListings] = useState<Listing[]>([]);
  const [category, setCategory] = useState("");
  const [q, setQ] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ title: "", category: "truck", price: "", condition: "used", location: "", description: "" });

  async function refresh() {
    setListings((await listListings(category || undefined, q || undefined)).listings);
  }
  useEffect(() => { void refresh(); /* eslint-disable-next-line */ }, [category]);

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (!form.title.trim()) return;
    setBusy(true);
    try {
      await createListing(form);
      setForm({ title: "", category: "truck", price: "", condition: "used", location: "", description: "" });
      setShowForm(false);
      await refresh();
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="mx-auto grid w-full max-w-3xl gap-6">
      <PageHeader
        title="Marketplace"
        subtitle="Buy and sell trucks, trailers, equipment, and parts."
        actions={
          <div className="flex gap-2">
            <Link to="/marketplace/mine"><Button variant="ghost">My listings</Button></Link>
            <Button onClick={() => setShowForm((v) => !v)}>{showForm ? "Cancel" : "Sell something"}</Button>
          </div>
        }
      />

      {showForm && (
        <Panel title="New listing">
          <form className="grid gap-3 sm:grid-cols-2" onSubmit={submit}>
            <input className={`${inputClass} sm:col-span-2`} placeholder="Title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} required />
            <select className={inputClass} value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} aria-label="Category">
              {LISTING_CATEGORIES.map((c) => <option key={c} value={c}>{labelize(c)}</option>)}
            </select>
            <select className={inputClass} value={form.condition} onChange={(e) => setForm({ ...form, condition: e.target.value })} aria-label="Condition">
              {LISTING_CONDITIONS.map((c) => <option key={c} value={c}>{labelize(c)}</option>)}
            </select>
            <input className={inputClass} placeholder="Price (blank = contact)" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} />
            <input className={inputClass} placeholder="Location" value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} />
            <textarea className={`${inputClass} sm:col-span-2`} rows={3} placeholder="Description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
            <Button type="submit" disabled={busy || !form.title.trim()} className="sm:col-span-2">{busy ? "Posting…" : "Post listing"}</Button>
          </form>
        </Panel>
      )}

      <div className="flex flex-wrap gap-1 border-b border-line">
        {[{ key: "", label: "All" }, ...LISTING_CATEGORIES.map((c) => ({ key: c, label: labelize(c) }))].map((t) => (
          <button key={t.key} type="button" onClick={() => setCategory(t.key)}
            className={`-mb-px border-b-2 px-3 py-2 text-sm font-bold transition-colors ${category === t.key ? "border-accent text-accent-strong" : "border-transparent text-muted hover:text-ink"}`}>
            {t.label}
          </button>
        ))}
      </div>
      <div className="flex gap-2">
        <input className={inputClass} placeholder="Search listings…" value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && refresh()} />
        <Button variant="ghost" onClick={refresh}>Search</Button>
      </div>

      {listings.length === 0 ? (
        <p className="text-sm text-muted">No listings here yet.</p>
      ) : (
        <div className="grid gap-3 sm:grid-cols-2">
          {listings.map((l) => (
            <Link key={l.id} to={`/marketplace/${l.id}`} className="grid gap-1 rounded-card border border-line bg-surface p-4 transition-colors hover:border-accent">
              <span className="font-display text-base font-black tracking-tight">{l.title}</span>
              <span className="font-mono text-sm font-bold text-accent-strong">{priceUsd(l.price_cents)}</span>
              <span className="font-mono text-[11px] text-muted">{labelize(l.category)}{l.location ? ` · ${l.location}` : ""}</span>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
